import type { ReadableDetailView } from "@studio-features/meta-crew/readable-details";
import type { AuditRow } from "../lib/types";
import { errorKindNames } from "../lib/format";

/**
 * 감사 기록·오류 상세를 사람이 읽는 모양으로 (시안 05be2fc, 요청서 23절 ReadableDetails).
 * 원본(JSON)은 관리자에게만 rawText 로 넘기고, 화면은 기본 접힌 "원본 보기"로만 보여 준다.
 * 모르는 키는 이름을 그대로 쓰고, 중첩 객체는 요약 대신 원본에만 둔다 (스키마를 짐작하지 않는다).
 */

const ACTIONS: Record<string, string> = {
  "auth.login": "로그인",
  "auth.logout": "로그아웃",
  "auth.password": "비밀번호 변경",
  "member.create": "회원 추가",
  "member.update": "회원 정보 변경",
  "member.disable": "회원 비활성화",
  "member.password_reset": "비밀번호 재설정",
  "member.roles": "역할 지정",
  "role.create": "역할 만들기",
  "role.update": "역할 고치기",
  "role.delete": "역할 지우기",
  "customer.create": "고객사 등록",
  "customer.update": "고객사 정보 변경",
  "customer.status": "고객사 상태 변경",
  "machine.create": "머신 등록",
  "machine.pairing_code": "연결 코드 발급",
  "machine.paired": "머신 연결",
  "machine.unpair": "페어링 해제",
  "machine.delete": "머신 삭제",
  "machine.pause": "배정 중지",
  "machine.resume": "배정 재개",
  "machine.retire": "머신 은퇴",
  "machine.update": "머신 업데이트",
  "machine.llm_mode": "AI 실행 방식 변경",
  "setting.update": "설정 변경",
  "task.create": "작업 등록",
  "task.cancel": "작업 취소",
  "release.publish": "설치 파일 배포",
  "release.withdraw": "설치 파일 회수",
};

const KEYS: Record<string, string> = {
  label: "이름",
  name: "이름",
  email: "이메일",
  status: "상태",
  role: "역할",
  roles: "역할",
  version: "버전",
  from: "이전",
  to: "이후",
  reason: "사유",
  key: "설정",
  value: "값",
  unbound: "풀린 역할 묶음",
  tokensRevoked: "폐기한 토큰",
  devicesDetached: "풀린 폰",
  commandId: "명령 ID",
  permissions: "권한",
  memberCount: "회원 수",
};

export const actionLabel = (action: string) => ACTIONS[action] ?? action;

const show = (v: unknown): string | null => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "예" : "아니오";
  if (typeof v === "number" || typeof v === "string") return String(v);
  if (Array.isArray(v) && v.every((x) => typeof x !== "object" || x === null)) return v.length ? v.join(", ") : "없음";
  return null;
};

function split(detail: Record<string, unknown> | null | undefined) {
  const fields: { label: string; value: string }[] = [];
  const changes: { label: string; before: string; after: string }[] = [];
  if (!detail) return { fields, changes };
  if ("from" in detail || "to" in detail) changes.push({ label: "값", before: show(detail.from) ?? "없음", after: show(detail.to) ?? "없음" });
  for (const [k, v] of Object.entries(detail)) {
    if (k === "from" || k === "to") continue;
    const s = show(v);
    if (s !== null) fields.push({ label: KEYS[k] ?? k, value: s });
  }
  return { fields, changes };
}

const raw = (v: unknown, staff: boolean) => (staff && v != null ? JSON.stringify(v, null, 2) : null);

/** 감사 기록 한 줄 요약 (표 안 짧은 글). */
export function auditSummary(r: AuditRow): string {
  const { fields, changes } = split(r.detail);
  const parts = [...changes.map((c) => `${c.before} → ${c.after}`), ...fields.slice(0, 2).map((f) => `${f.label} ${f.value}`)];
  return parts.join(" · ");
}

export function auditDetailView(r: AuditRow, staff: boolean): ReadableDetailView {
  const { fields, changes } = split(r.detail);
  const who = r.actorName ?? (r.actorKind === "system" ? "시스템" : r.actorKind === "machine" ? "머신" : "-");
  return {
    title: actionLabel(r.action),
    summary: `${who} · ${new Date(r.at).toLocaleString("ko-KR")}`,
    fields: [...(r.targetType ? [{ label: "대상", value: `${r.targetType}${r.targetId ? ` ${r.targetId.slice(0, 8)}` : ""}` }] : []), ...fields],
    changes,
    rawText: raw(r.detail, staff),
  };
}

/** 실행 오류 상세 (실행 상세·실행 이력). */
export function errorDetailView(o: { errorKind: string | null; error: string | null; errorDetail?: unknown }, staff: boolean): ReadableDetailView {
  const detail = o.errorDetail && typeof o.errorDetail === "object" && !Array.isArray(o.errorDetail) ? (o.errorDetail as Record<string, unknown>) : null;
  const { fields, changes } = split(detail);
  return {
    title: o.errorKind ? (errorKindNames[o.errorKind] ?? o.errorKind) : "오류",
    summary: o.error ?? "자세한 사유가 없습니다.",
    fields,
    changes,
    rawText: raw(o.errorDetail, staff),
  };
}
